import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";

import DropdownMenu from "../../library/DropdownMenu";

const SEARCH_FIELDS = [
  { key: "email", label: "Email", placeholder: "Search by email" },
  { key: "name", label: "Name", placeholder: "Search by name" },
  { key: "phone", label: "Phone", placeholder: "Search by phone number" },
  {
    key: "promo_code",
    label: "Promo code",
    placeholder: "Search by promo code",
  },
];

const buttonSx = {
  minHeight: 36,
  color: "text.primary",
  borderColor: "divider",
  "&:hover": {
    borderColor: "primary.main",
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
  },
};

export default function AccountsFilters({
  filters,
  onChange,
  onReset,
  userTypeOptions,
  userRoleOptions,
  userStatusOptions,
  onSavedViews,
  savedViewsOpen,
  onSaveView,
  onRefresh,
  loading,
  onExport,
  exporting,
}) {
  const [searchField, setSearchField] = useState("email");
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [filterMenu, setFilterMenu] = useState(null);
  const searchInput = useRef(null);
  const fields = [
    ...SEARCH_FIELDS,
    { key: "user_type", label: "User type", options: userTypeOptions },
    { key: "status", label: "Account status", options: userStatusOptions },
    {
      key: "subscription_status",
      label: "Subscription",
      options: [
        { value: "active", label: "Active" },
        { value: "trialing", label: "Trialing" },
        { value: "past_due", label: "Past due" },
        { value: "cancelled", label: "Cancelled" },
        { value: "expired", label: "Expired" },
      ],
    },
    { key: "user_role", label: "User role", options: userRoleOptions },
    {
      key: "hitpay_verified",
      label: "HitPay verification",
      empty: "all",
      options: [
        { value: "true", label: "Verified" },
        { value: "false", label: "Not verified" },
      ],
    },
    {
      key: "has_promo",
      label: "Promo source",
      empty: "all",
      options: [
        { value: "true", label: "With promo" },
        { value: "false", label: "Without promo" },
      ],
    },
  ];
  const activeFields = fields.filter((field) => {
    const value = filters[field.key];
    return value?.trim() && value !== (field.empty || "");
  });
  const search = SEARCH_FIELDS.find((field) => field.key === searchField);
  const menuField = fields.find((field) => field.key === filterMenu?.key);
  const moreFields = fields.filter((field) =>
    ["user_role", "hitpay_verified", "has_promo"].includes(field.key),
  );
  const moreCount = moreFields.filter((field) =>
    activeFields.includes(field),
  ).length;
  const setFilter = (key, value) =>
    onChange((current) => ({ ...current, [key]: value }));
  const valueLabel = (field) =>
    field.options?.find((option) => option.value === filters[field.key])
      ?.label || filters[field.key]?.trim();
  const openFilter = (event, key) =>
    setFilterMenu({ anchor: event.currentTarget, key });

  return (
    <Paper
      component='section'
      aria-label='Account filters'
      elevation={0}
      sx={{
        flexShrink: 0,
        minHeight: 0,
        maxHeight: "50%",
        overflowY: "auto",
        overscrollBehavior: "contain",
        border: 1,
        borderColor: "divider",
        borderRadius: "12px 12px 0 0",
        "& .MuiButton-root": {
          textTransform: "none",
          borderRadius: 1.5,
          fontWeight: 600,
        },
      }}
    >
      <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={1.5}
          flexWrap='wrap'
          alignItems={{ md: "center" }}
        >
          <TextField
            inputRef={searchInput}
            fullWidth
            size='small'
            value={filters[searchField] || ""}
            placeholder={search.placeholder}
            onChange={(event) => setFilter(searchField, event.target.value)}
            inputProps={{
              "aria-label": search.placeholder,
              maxLength: searchField === "promo_code" ? 32 : undefined,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Button
                    size='small'
                    color='inherit'
                    endIcon={<ExpandMoreIcon />}
                    aria-label={`Search field: ${search.label}`}
                    aria-haspopup='menu'
                    aria-expanded={Boolean(searchAnchor)}
                    onClick={(event) => setSearchAnchor(event.currentTarget)}
                    sx={{ mr: 0.5, whiteSpace: "nowrap", minWidth: 0 }}
                  >
                    {search.label}
                  </Button>
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: filters[searchField] ? (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    aria-label={`Clear ${search.label.toLowerCase()} search`}
                    onClick={() => setFilter(searchField, "")}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: { md: "1 1 320px" },
              minWidth: 0,
              "& .MuiOutlinedInput-root": { borderRadius: 2, pl: 0.5 },
            }}
          />
          <Stack direction='row' gap={1} flexWrap='wrap'>
            {["status", "subscription_status"].map((key) => {
              const field = fields.find((item) => item.key === key);
              return (
                <Button
                  key={key}
                  variant='outlined'
                  size='small'
                  endIcon={<ExpandMoreIcon />}
                  onClick={(event) => openFilter(event, key)}
                  aria-haspopup='menu'
                  aria-expanded={filterMenu?.key === key}
                  sx={{
                    ...buttonSx,
                    ...(filters[key] && {
                      color: "primary.main",
                      borderColor: "primary.main",
                    }),
                  }}
                >
                  {field.label}
                  {filters[key] ? `: ${valueLabel(field)}` : ""}
                </Button>
              );
            })}
            <Button
              variant='outlined'
              size='small'
              startIcon={<FilterListIcon />}
              onClick={(event) => openFilter(event, "more")}
              aria-haspopup='menu'
              aria-expanded={Boolean(
                filterMenu &&
                !["status", "subscription_status", "user_type"].includes(
                  filterMenu.key,
                ),
              )}
              sx={{
                ...buttonSx,
                ...(moreCount > 0 && {
                  color: "primary.main",
                  borderColor: "primary.main",
                }),
              }}
            >
              More filters{moreCount ? ` (${moreCount})` : ""}
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction='row'
          justifyContent='space-between'
          gap={1.5}
          flexWrap='wrap'
          alignItems='center'
        >
          <Stack
            direction='row'
            gap={0.75}
            flexWrap='wrap'
            alignItems='center'
            role='group'
            aria-label='User type'
            sx={{ minWidth: 0 }}
          >
            <Typography variant='body2' color='text.secondary' sx={{ mr: 0.5 }}>
              User type
            </Typography>
            {[{ value: "", label: "All" }, ...userTypeOptions].map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                size='small'
                aria-pressed={filters.user_type === option.value}
                onClick={() => setFilter("user_type", option.value)}
                color={
                  filters.user_type === option.value ? "primary" : "default"
                }
                variant={
                  filters.user_type === option.value ? "filled" : "outlined"
                }
                sx={{
                  fontWeight: filters.user_type === option.value ? 600 : 400,
                }}
              />
            ))}
          </Stack>
          <Stack
            direction='row'
            gap={0.75}
            alignItems='center'
            flexWrap='wrap'
            role='group'
            aria-label='Account list actions'
            sx={{ minWidth: 0, ml: { sm: "auto" } }}
          >
            <Button
              size='small'
              startIcon={<BookmarkBorderIcon />}
              onClick={onSavedViews}
              aria-haspopup='menu'
              aria-expanded={savedViewsOpen}
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                minHeight: { xs: 40, sm: 36 },
              }}
            >
              Saved filters
            </Button>
            <Tooltip
              title={
                exporting
                  ? "Exporting organizations..."
                  : "Export organizations overview"
              }
            >
              <span>
                <Button
                  size='small'
                  aria-label={
                    exporting
                      ? "Exporting organizations"
                      : "Export organizations overview"
                  }
                  onClick={onExport}
                  disabled={exporting}
                  startIcon={
                    exporting ? (
                      <CircularProgress size={18} color='inherit' />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  sx={{
                    color: "text.secondary",
                    minWidth: { xs: 40, sm: 64 },
                    minHeight: { xs: 40, sm: 36 },
                    px: 1,
                    "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
                  }}
                >
                  <Box
                    component='span'
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {exporting ? "Exporting..." : "Export"}
                  </Box>
                </Button>
              </span>
            </Tooltip>
            <Tooltip title='Refresh accounts'>
              <span>
                <IconButton
                  aria-label='Refresh accounts'
                  onClick={onRefresh}
                  disabled={loading}
                  size='small'
                  sx={{ width: { xs: 40, sm: 36 }, height: { xs: 40, sm: 36 } }}
                >
                  <RefreshIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      {activeFields.length > 0 && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
          }}
        >
          <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
            <Typography variant='caption' color='text.secondary'>
              Active ({activeFields.length})
            </Typography>
            {activeFields.map((field) => (
              <Chip
                key={field.key}
                size='small'
                variant='outlined'
                label={`${field.label}: ${valueLabel(field)}`}
                title={`${field.label}: ${valueLabel(field)}`}
                onDelete={() => setFilter(field.key, field.empty || "")}
                onClick={(event) => {
                  if (field.options) openFilter(event, field.key);
                  else {
                    setSearchField(field.key);
                    searchInput.current?.focus();
                  }
                }}
                sx={{ maxWidth: "100%", bgcolor: "background.paper" }}
              />
            ))}
            <Button size='small' onClick={onReset}>
              Clear all
            </Button>
            <Button
              size='small'
              startIcon={<BookmarkAddOutlinedIcon />}
              onClick={onSaveView}
              sx={{ ml: { sm: "auto" } }}
            >
              Save current filters
            </Button>
          </Stack>
          {SEARCH_FIELDS.filter((field) => filters[field.key]?.trim()).length >
            1 && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: "block", mt: 0.75 }}
            >
              Results match all active filters. Click a search tag to edit it.
            </Typography>
          )}
        </Box>
      )}

      <DropdownMenu
        anchorEl={searchAnchor}
        open={Boolean(searchAnchor)}
        onClose={() => setSearchAnchor(null)}
        title='Search field'
        value={searchField}
        options={SEARCH_FIELDS.map((field) => ({
          value: field.key,
          label: field.label,
          description: filters[field.key]?.trim()
            ? "Filter applied"
            : undefined,
        }))}
        onSelect={(key) => {
          setSearchField(key);
          setSearchAnchor(null);
        }}
      />
      <DropdownMenu
        anchorEl={filterMenu?.anchor || null}
        open={Boolean(filterMenu)}
        onClose={() => setFilterMenu(null)}
        title={menuField?.label || "More filters"}
        mode={menuField ? "select" : "navigate"}
        value={menuField ? filters[menuField.key] : undefined}
        options={
          menuField
            ? [
                { value: menuField.empty || "", label: "All" },
                ...menuField.options,
              ]
            : moreFields.map((field) => ({
                value: field.key,
                label: field.label,
                description: activeFields.includes(field)
                  ? valueLabel(field)
                  : "All",
              }))
        }
        onSelect={(value) => {
          if (menuField) {
            setFilter(menuField.key, value);
            setFilterMenu(null);
          } else {
            setFilterMenu((current) => ({ ...current, key: value }));
          }
        }}
        onBack={
          moreFields.includes(menuField)
            ? () => setFilterMenu((current) => ({ ...current, key: "more" }))
            : undefined
        }
        backLabel='Back to more filters'
      />
    </Paper>
  );
}
